import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { ShieldCheck, ArrowLeft, CheckCircle2, Lock, Copy, Share2 } from 'lucide-react';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';
import { isBlockedEmailProvider } from '../utils/isBlockedEmailProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WaitlistPage — public entrypoint for people without an invite.
 *
 * GhostComm is invite-only on purpose. Instead of a dead-end for strangers,
 * this page lets them drop an email + short note and get queued. An existing
 * member will eventually see their request and can mint them an invite
 * code (48h, single-use). We never email them automatically — the approver
 * shares the code out-of-band. That keeps us out of the "send bulk mail"
 * business (which would otherwise need a 3rd-party provider and leak
 * metadata).
 *
 * Layout mirrors the split-screen Auth pages so the aesthetic stays
 * consistent across every public entrypoint.
 */
const BLURB_MAX = 280;

export const WaitlistPage = () => {
  const [email, setEmail] = useState('');
  const [blurb, setBlurb] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { position, share_token } on success
  const emailBlocked = isBlockedEmailProvider(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    // Cheap client-side shape check — real validation happens server-side.
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (blurb.length > BLURB_MAX) {
      toast.error(`Keep your note under ${BLURB_MAX} characters`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: trimmed,
        blurb: blurb.trim() || null,
      });
      setResult(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit request. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const done = !!result;
  // Public share link goes to the crawlable `/api/share/...` endpoint on the
  // backend — it serves Open Graph + Twitter Card meta tags to social
  // scrapers and bounces humans into the SPA with a meta-refresh. Without
  // this indirection, Twitter/Discord/Slack would just show a bare URL.
  const statusUrl = result?.share_token
    ? `${window.location.origin}/api/share/waitlist/${result.share_token}`
    : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(statusUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'I just joined the Government Shield waitlist',
          text: result?.position
            ? `I'm #${result.position} on the Government Shield waitlist — an encrypted, invite-only messenger.`
            : "I just joined the Government Shield waitlist — an encrypted, invite-only messenger.",
          url: statusUrl,
        });
      } catch {
        // user cancelled — no-op
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: '#05050A' }}>
      {/* Visual Side — mirrors LoginPage so we stay on-brand */}
      <div
        className="hidden lg:flex flex-col justify-center items-center relative overflow-hidden"
        style={{ backgroundColor: '#0F1115' }}
      >
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10 p-12 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-8 glow-primary">
            <ShieldCheck className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>
          <h1
            className="text-4xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Government Shield
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            End-to-end encrypted messaging and voice for people who take privacy seriously.
            Invite-only by design.
          </p>
          <div className="mt-8 space-y-3 text-left text-sm text-slate-500">
            <p className="flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 text-primary/70 shrink-0" />
              <span>No tracking, no message archives past 24 hours, no third-party providers.</span>
            </p>
            <p className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-primary/70 shrink-0" />
              <span>Keys live on your device. We can't read your messages even if we wanted to.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex flex-col justify-center items-center p-6 lg:p-12">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur border-border">
          <CardHeader>
            <CardTitle
              className="text-2xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Request access
            </CardTitle>
            <CardDescription className="text-slate-400">
              An existing member will review your request and, if approved,
              share a single-use invite code with you directly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div
                className="space-y-5 py-4 text-center"
                data-testid="waitlist-success"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
                </div>
                <div className="space-y-2">
                  <p className="text-white font-medium">You're on the list</p>
                  {result?.position ? (
                    <p className="text-sm text-slate-400 leading-relaxed">
                      You're{' '}
                      <span
                        className="text-primary font-mono text-lg font-medium"
                        data-testid="waitlist-position"
                      >
                        #{result.position}
                      </span>{' '}
                      in the pending queue. A current member will reach out to{' '}
                      <span className="text-slate-200 font-mono">{email}</span>{' '}
                      with an invite code if approved. This is peer-reviewed,
                      not automated — give it a few days.
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400 leading-relaxed">
                      A current member will reach out to{' '}
                      <span className="text-slate-200 font-mono">{email}</span>{' '}
                      with an invite code if approved. This is peer-reviewed,
                      not automated — give it a few days.
                    </p>
                  )}
                </div>

                {statusUrl && (
                  <div className="space-y-2 pt-2">
                    <div className="flex gap-2 p-2 rounded-lg bg-secondary/30 border border-border">
                      <code
                        className="flex-1 text-xs text-slate-300 truncate self-center px-1 font-mono text-left"
                        data-testid="waitlist-status-url"
                      >
                        {statusUrl}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={copyLink}
                        data-testid="waitlist-copy-link"
                        className="shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={share}
                        data-testid="waitlist-share-btn"
                      >
                        <Share2 className="w-3.5 h-3.5 mr-2" />
                        Share my spot
                      </Button>
                      <Link
                        to={`/waitlist/status/${result.share_token}`}
                        className="flex-1"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          data-testid="waitlist-view-status"
                        >
                          View status
                        </Button>
                      </Link>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      Your status link is email-free — safe to share publicly
                      without leaking your address.
                    </p>
                  </div>
                )}

                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline pt-2"
                  data-testid="waitlist-back-to-login"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="waitlist-form">
                <div className="space-y-2">
                  <Label htmlFor="waitlist-email" className="text-slate-300">
                    Email
                  </Label>
                  <Input
                    id="waitlist-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@somewhere.com"
                    required
                    data-testid="waitlist-email-input"
                    aria-invalid={emailBlocked || undefined}
                    className={emailBlocked ? 'border-red-500/60 focus:border-red-500' : ''}
                  />
                  <p className="text-xs text-slate-500">
                    Used only so an existing member can send you a code. We never
                    email you marketing or newsletters.
                  </p>
                  <p
                    className={`text-[11px] leading-relaxed flex items-start gap-1.5 ${
                      emailBlocked ? 'text-red-400' : 'text-amber-400/80'
                    }`}
                    data-testid="waitlist-email-provider-notice"
                    data-blocked={emailBlocked || undefined}
                    role={emailBlocked ? 'alert' : undefined}
                  >
                    <span className={`font-bold mt-px ${emailBlocked ? 'text-red-400' : 'text-amber-400'}`}>
                      !
                    </span>
                    <span>
                      {emailBlocked ? (
                        <>
                          <span className="font-semibold">
                            This address won't work — Microsoft drops our mail.
                          </span>{' '}
                          Your invite code would never arrive. Try Gmail, Proton Mail, Tutanota,
                          Fastmail, or a work / university address.{' '}
                          <Link
                            to="/help/email-providers"
                            className="underline decoration-dotted hover:text-red-300 transition-colors"
                            data-testid="waitlist-email-why-link"
                          >
                            Why?
                          </Link>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold">Outlook, Hotmail, Live, and MSN are not supported.</span>{' '}
                          Microsoft's filters silently drop our mail — your invite code
                          would never arrive. Use Gmail, Proton Mail, Tutanota, Fastmail,
                          or a work / university address.
                        </>
                      )}
                    </span>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="waitlist-blurb" className="text-slate-300">
                    Why you want in{' '}
                    <span className="text-slate-500 font-normal">(optional)</span>
                  </Label>
                  <textarea
                    id="waitlist-blurb"
                    value={blurb}
                    onChange={(e) => setBlurb(e.target.value.slice(0, BLURB_MAX))}
                    placeholder="A sentence or two so members know who they're approving."
                    rows={4}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="waitlist-blurb-input"
                  />
                  <div className="flex justify-end">
                    <span
                      className={`text-xs ${
                        blurb.length > BLURB_MAX - 20
                          ? 'text-amber-400'
                          : 'text-slate-500'
                      }`}
                      data-testid="waitlist-blurb-counter"
                    >
                      {blurb.length} / {BLURB_MAX}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || emailBlocked}
                  data-testid="waitlist-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Request an invite'}
                </Button>
              </form>
            )}

            {!done && (
              <div className="mt-6 text-center text-sm text-slate-400">
                Already have an invite?{' '}
                <Link
                  to="/login"
                  className="text-primary hover:underline"
                  data-testid="waitlist-signin-link"
                >
                  Sign in
                </Link>
                <div className="mt-2 text-xs text-slate-500">
                  Curious first?{' '}
                  <Link
                    to="/manifesto"
                    className="text-slate-500 hover:text-slate-300 hover:underline"
                    data-testid="waitlist-manifesto-link"
                  >
                    Read our principles
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <GovernmentDisclaimer className="mt-6 max-w-md w-full" />
      </div>
    </div>
  );
};

export default WaitlistPage;
